import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { asyncScheduler, Observable } from 'rxjs';
import { map, take, observeOn } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { BillVisitViewModel, VisitInsuranceBillingInfoModel } from '../../../../../../projects/data/src/public-api';
import { QueuesWrapperService } from '../../../../core/services/service-wrappers/queues-wrapper.service';
import { VisitWrapperService } from '../../../../core/services/service-wrappers/visit-wrapper.service';
import { saveAs } from 'file-saver';
import { FrequencyCodeLookupService } from '@core/services/lookup/frequency-code-lookup.service';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { MAT_SELECT_CONFIG } from '@angular/material/select';

@Component({
  standalone: false,
  selector: 'app-bill-claim',
  templateUrl: './bill-claim.component.html',
  styleUrls: ['./bill-claim.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'code-dropdown-custom' }
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'code-dropdown-custom' }
    }
  ]
})
export class BillClaimComponent implements OnInit {
  formGroup = new UntypedFormGroup({});
  formInitialized = false;
  saving = false;
  isRebill: boolean = false;
  visit: VisitInsuranceBillingInfoModel;
  primaryInsurance: string;
  insuranceArray: Array<{ label: string; value: string; type: string }> = new Array<{
    label: string;
    value: string;
    type: string;
  }>();
  claimTypes = ['Scheduled', 'Print On-Demand'];
  insuranceICNList = [];
  ICNDict = {};
  icnFilter = '';
  errorMessage = '';
  saveText = 'Save';
  selectedType = 'Scheduled';
  selectedInsuranceType = 'Electronic';
  loading = true;
  invalidState = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private injector: Injector,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<BillClaimComponent>,
    private service: QueuesWrapperService,
    private visitService: VisitWrapperService,
    private formBuilder: UntypedFormBuilder,
    private frequencyCodeService: FrequencyCodeLookupService
  ) {}

  ngOnInit(): void {
    if (this.data?.rebill) {
      this.isRebill = true;
    }

    this.formGroup = this.formBuilder.group({
      insurance: new UntypedFormControl(null, Validators.required),
      isVoided: new UntypedFormControl(false),
      isCorrected: new UntypedFormControl(false),
      originalICN: new UntypedFormControl(null),
      claimType: new UntypedFormControl(this.selectedType),
      isHistory: new UntypedFormControl(true),
      printImage: new UntypedFormControl(true),
      frequencyCodeId: new UntypedFormControl(null),
      delayReasonCode: new UntypedFormControl(null)
    });
    this.setupForm();

    this.visitService
      .apiV1VisitInsuranceForBillingIdGet(this.data?.visitId)
      .pipe(
        map((x) => {
          return x;
        })
      )
      .subscribe((visit) => {
        this.visit = visit;
        if (this.visit?.primaryInsuranceId !== null)
          this.insuranceArray.push({
            value: this.visit?.primaryInsuranceId,
            label: this.visit?.primaryCarrierDisplay,
            type: this.visit?.primaryClaimType
          });
        if (this.visit?.secondaryInsuranceId !== null)
          this.insuranceArray.push({
            value: this.visit?.secondaryInsuranceId,
            label: this.visit?.secondaryCarrierDisplay,
            type: this.visit?.secondaryClaimType
          });
        if (this.visit?.tertiaryInsuranceId !== null)
          this.insuranceArray.push({
            value: this.visit?.tertiaryInsuranceId,
            label: this.visit?.tertiaryCarrierDisplay,
            type: this.visit?.tertiaryClaimType
          });
        if (this.visit?.paymentIcns.length > 0) {
          this.visit.paymentIcns.forEach((icn) => {
            let insuranceId = icn.insuranceId;
            if (insuranceId !== null) {
              let icns = this.ICNDict[insuranceId];
              if (icns === undefined) {
                this.ICNDict[insuranceId] = [icn.internalControlNumber];
              } else {
                this.ICNDict[insuranceId].push(icn.internalControlNumber);
              }
            }
          });
        }
        this.loading = false;
      });
  }

  setupForm() {
    this.formInitialized = true;
  }

  insuranceSelectionChange($event) {
    let insuranceId = $event.value;
    this.selectedInsuranceType = this.insuranceArray.find((x) => x.value === insuranceId).type;
    this.insuranceICNList = [];
    if (this.ICNDict[insuranceId] !== undefined) {
      this.ICNDict[insuranceId].forEach((icn) => {
        this.insuranceICNList.push({ label: icn, value: icn });
      });
    }
    if (this.selectedInsuranceType === 'ElectronicInstitutional') {
      this.formGroup.get('claimType').setValue('Scheduled');
      this.selectedType = 'Scheduled';
      this.saveText = 'Save';
    }
  }

  claimCheckboxChanged($event) {
    var id = $event.source.id;
    if ($event.checked) {
      if (id.indexOf('isCorrected') >= 0) this.formGroup.controls['isVoided'].setValue(false);
      else this.formGroup.controls['isCorrected'].setValue(false);
    }
    let voided = this.formGroup.get('isVoided').value;
    let corrected = this.formGroup.get('isCorrected').value;
    let icn = this.formGroup.get('originalICN');
    if (corrected || voided) {
      icn.setValidators(Validators.required);
    } else {
      icn.clearValidators();
    }
    (document.getElementById('originalICNText') as HTMLElement)?.focus();
  }

  icnFilterChanged($event: any) {
    this.icnFilter = $event.srcElement.value;
  }

  typeChanged($event) {
    this.selectedType = $event.value;
    if (this.selectedType === 'Print On-Demand') {
      this.saveText = 'Print';
    } else {
      this.saveText = 'Save';
    }
  }

  save() {
    if (!this.saving && this.visit) {
      if (this.selectedType === 'Print On-Demand' && this.selectedInsuranceType === 'ElectronicInstitutional') {
        this.notificationService.error('Electronic Institutional claims cannot be printed. Please select Scheduled.');
        return;
      }
      this.errorMessage = '';
      if (this.formGroup) {
        this.saving = true;
        const formData: BillVisitViewModel = {
          patientId: this.visit.patientId,
          visitId: this.visit.id,
          procedureTransactionIds: this.data.procedureTransactionIds || [],
          insuranceId: this.formGroup.get('insurance').value,
          isCorrected: this.formGroup.get('isCorrected').value,
          originalICN: this.formGroup.get('originalICN').value ?? this.icnFilter,
          isVoided: this.formGroup.get('isVoided').value,
          printImage: this.formGroup.get('printImage').value,
          markAsBilled: this.formGroup.get('isHistory').value,
          frequencyCodeId: this.formGroup.get('frequencyCodeId').value,
          delayReasonCode: this.formGroup.get('delayReasonCode').value
        };
        if (this.selectedInsuranceType === 'Electronic' || this.selectedInsuranceType === 'Paper') {
          formData.frequencyCodeId = null;
        }
        if (this.selectedType === 'Print On-Demand') {
          this.print(formData);
        } else {
          let saveObservable: Observable<any>;
          let notification;
          if (this.formGroup) {
            notification = 'Bill Claim Added';
            saveObservable = this.service.apiV1BillingBillVisitPost(formData).pipe(
              map((x: any) => {
                return x;
              })
            );
          }
          saveObservable
            .pipe(take(1), observeOn(asyncScheduler))
            .subscribe(
              (response) => {
                this.dialogRef.close(true);
                this.notificationService.success(notification);
              },
              (err) => {
                if (typeof err.error == 'string') this.errorMessage = 'Error: ' + err.error;
                else this.notificationService.error('Error Saving Bill Claim');
                this.finishSaving();
              }
            );
        }
      }
    }
  }

  print(formData: BillVisitViewModel) {
    this.service
      .apiV1BillingHcfaPost([formData])
      .pipe(take(1), observeOn(asyncScheduler))
      .subscribe(
        (fileData) => {
          saveAs(fileData, 'patient-hcfa.pdf');
          this.dialogRef.close();
        },
        (err) => {
          if (err.error instanceof Blob) {
            err.error.text().then((text) => {
              this.invalidState = true;
              let errorMsg = JSON.parse(text).detail ?? JSON.parse(text).title;
              this.notificationService.error(errorMsg);
            });
          } else {
            this.notificationService.error('Error Printing HCFA.');
          }
          this.finishSaving();
        }
      );
  }

  private finishSaving() {
    setTimeout(() => {
      this.saving = false;
    });
  }

  delayReasonCodes = [
    { code: '1', value: '1', label: 'Proof of Eligibility Unknown or Unavailable' },
    { code: '2', value: '2', label: 'Litigation' },
    { code: '3', value: '3', label: 'Authorization Delays' },
    { code: '4', value: '4', label: 'Delay in Certifying Provider' },
    { code: '5', value: '5', label: 'Delay in Supplying Billing Forms' },
    { code: '6', value: '6', label: 'Delay in Delivery of Custom-made Appliances' },
    { code: '7', value: '7', label: 'Third Party Processing Delay' },
    { code: '8', value: '8', label: 'Delay in Eligibility Determination' },
    {
      code: '9',
      value: '9',
      label: 'Original Claim Rejected or Denied Due to a Reason Unrelated to the Billing Limitation Rules'
    },
    { code: '10', value: '10', label: 'Administration Delay in the Prior Approval Process' },
    { code: '11', value: '11', label: 'Other' },
    { code: '15', value: '15', label: 'Natural Disaster' }
  ];
}

